// One place for the API base URL and the server-side fetch shape.
//
// The same `process.env.NEXT_PUBLIC_API_BASE_URL ?? <render fallback>` line was
// copy-pasted into eight files before this existed. New code imports from here;
// the older call sites are being migrated opportunistically rather than in one
// churn-heavy pass.
//
// Keep reading the same env var: next.config.ts derives the CSP connect-src from
// it, so pointing it at localhost during development also opens the CSP. Any
// hardcoded host here would silently break that.

export const API_BASE =
  process.env.NEXT_PUBLIC_API_BASE_URL ?? "https://laptop-search-tool-dev.onrender.com";

export type GetJsonOptions = {
  /** Seconds to cache the response for. Omit for the route's default. */
  revalidate?: number;
  /** Milliseconds before the request is aborted. */
  timeoutMs?: number;
};

/**
 * Server-side GET returning parsed JSON, or null when the resource genuinely
 * does not exist.
 *
 * The 404-vs-throw split matters for prerendering: a 404 is a real "no such
 * page" and the caller turns it into notFound(), but any other failure throws so
 * Next's staticGenerationRetryCount retries instead of baking a transient API
 * blip into a permanently cached 404. This mirrors what the /spec page has
 * always done by hand.
 */
export async function getJson<T>(path: string, opts: GetJsonOptions = {}): Promise<T | null> {
  const { revalidate = 3600, timeoutMs = 15000 } = opts;

  const res = await fetch(`${API_BASE}${path}`, {
    next: { revalidate },
    signal: AbortSignal.timeout(timeoutMs),
  });

  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`API GET ${path} failed (${res.status})`);
  return (await res.json()) as T;
}
